import { clientsApi } from "#/api/clientsApi";
import ActionButton from "#/components/buttons/ActionButton";

import Pagination from "#/components/buttons/Pagination";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { Button, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconImageInPicture, IconPlus } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CreateClientDrawer from "./components/CreateNewClientDrawer";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const ClientsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite } = usePermission();
  const [
    createDrawerOpened,
    { open: openCreateDrawer, close: closeCreateDrawer },
  ] = useDisclosure(false);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "id", value: t("clients.columns.id") },
    { key: "logo", value: t("clients.columns.logo") },
    { key: "name", value: t("clients.columns.name") },
    { key: "color", value: t("clients.columns.color") },
    { key: "contactEmail", value: t("clients.columns.contactEmail") },
    { key: "createdAt", value: t("clients.columns.createdAt") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
  } = useQuery({
    queryKey: ["clients", sortBy, sortDirection, page, limit],
    queryFn: async () => {
      try {
        const response = await clientsApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No items returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("clients.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                openCreateDrawer();
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("clients.createNewClient")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[2, 4, 5]}
          onSortChange={(e: TableSortProps) => {
            // the values are correctly getting updated in state
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 5,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td style={{ minWidth: '100px' }}>{item.id ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '100px' }}>
                  <div className="w-10 h-10 relative flex items-center justify-center">
                    {item.logo ? (
                      <img
                        src={item.logo ?? ""}
                        alt="logo"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <IconImageInPicture size={40} />
                    )}
                  </div>
                </Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '180px' }}>{item.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>
                  <div className="flex items-center gap-4">
                    <div
                      className="w-8 h-8 rounded-lg border border-slate-200"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs text-slate-500">{item.color}</span>
                  </div>
                </Table.Td>
                <Table.Td style={{ minWidth: '200px' }}>{item?.contactEmail ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '140px' }}>
                  <span>
                    {formatDate(item.createdAt, client)}
                    <br />
                    {formatTime(item.createdAt, client)}
                  </span>
                </Table.Td>
                <Table.Td style={{ minWidth: '100px' }}>
                  <div className="w-full flex items-center justify-center">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/clients/${item.id}`);
                      }}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <CreateClientDrawer
        opened={createDrawerOpened}
        onClose={() => {
          closeCreateDrawer();
        }}
        onSuccess={(clientId) => {
          closeCreateDrawer();
          void navigate(`/clients/${clientId}`);
        }}
      />
    </div>
  );
};

export default ClientsPage;
